import { Bell } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { HtmlSeguro } from "@/components/shared/HtmlSeguro";
import { useDismissNotificacao, useDismissTodas, useNotificacoes } from "@/features/notificacao/api";
import { Link } from "react-router-dom";

// Sino do header, compartilhado pelas duas skins (AppHeader e LegacyTopBar).
// Porte do dropdown de notificações do index.html legado + MainCtrl: polling de
// 60s, clique numa notificação marca como lida, "marcar todas" sem confirmação
// (a confirmação fica na tela /Notificacao/Consulta, como no legado).
const TAMANHO_SINO = 10;

export function NotificacoesMenu({ triggerClassName }: { triggerClassName?: string }) {
  const { data } = useNotificacoes("nao-lidas", TAMANHO_SINO, { poll: true });
  const dismiss = useDismissNotificacao();
  const dismissTodas = useDismissTodas();
  const total = data?.totalElements ?? 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={total > 0 ? `Notificações (${total} não lidas)` : "Notificações"}
        title="Notificações"
        className={cn("relative flex h-9 w-9 items-center justify-center rounded-full outline-none transition-colors", triggerClassName)}
      >
        <Bell className="h-5 w-5" />
        {total > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-white">
            {total > 99 ? "99+" : total}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 rounded-xl">
        <div className="flex items-center justify-between gap-2 px-3 py-2">
          <span className="text-sm font-semibold">
            {total > 0 ? `Você tem ${total} notificações` : "Notificações"}
          </span>
          {total > 0 && (
            <button
              type="button"
              className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
              disabled={dismissTodas.isPending}
              onClick={() => dismissTodas.mutate()}
            >
              Marcar todas como lidas
            </button>
          )}
        </div>
        <DropdownMenuSeparator />
        {total === 0 ? (
          <DropdownMenuItem disabled className="justify-center text-sm text-muted-foreground">
            Nenhuma notificação
          </DropdownMenuItem>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {data!.content.map((n) => (
              <DropdownMenuItem
                key={n.id}
                // Mantém o menu aberto, como o stopPropagation do legado: dá pra ir lendo e marcando em sequência.
                onSelect={(e) => {
                  e.preventDefault();
                  dismiss.mutate(n.id);
                }}
                className="flex cursor-pointer flex-col items-start gap-0.5"
              >
                <span className="text-sm font-medium">{n.tituloPortal}</span>
                <HtmlSeguro html={n.corpoPortal} className="line-clamp-3 text-xs text-muted-foreground" />
              </DropdownMenuItem>
            ))}
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/Notificacao/Consulta" className="w-full cursor-pointer justify-center text-sm font-medium text-primary">
            Ver todas
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
